import { lazy } from "react";

const Stocks = lazy(() =>
  import("../Inventory/Stock/StockList/Stocks")
);
const GoodsReceipts = lazy(() =>
  import("../Inventory/Stock/GoodsReceiptList/GoodsReceipts")
);
const ViewGoodsReceipt = lazy(() =>
  import("../Inventory/Stock/ViewGoodsReceipt/ViewGoodsReceipt")
);
const OrderAndReceiveForm = lazy(() =>
  import("../Inventory/Stock/OrderAndReceiveForm/OrderAndReceiveForm")
);
const ViewOrder = lazy(() =>
  import("../Inventory/Stock/ViewOrder/ViewOrder")
);
const ReceivedDeliveryForm = lazy(() =>
  import("../Inventory/Stock/ReceivedDeliveryForm/ReceivedDeliveryForm")
);
const StockTransferHistory = lazy(() =>
  import("../Inventory/StockTransfer/History/StockTransferHistory")
);
const StockTransferForm = lazy(() =>
  import("../Inventory/StockTransfer/Form/StockTransferForm")
);
const SpecialOrders = lazy(() =>
  import("../Inventory/SpecialOrder/SpecialOrders")
);
const SpecialOrderForm = lazy(() =>
  import("../Inventory/SpecialOrder/SpecialOrderForm")
);
const StockReturns = lazy(() =>
  import("../Inventory/StockReturn/List/StockReturns")
);
const StockReturnForm = lazy(() =>
  import("../Inventory/StockReturn/Form/PurchaseReturnForm")
);
const ViewReturnStock = lazy(() =>
  import("../Inventory/StockReturn/ViewReturn/ViewReturnStock")
);
const PrintLabelHistory = lazy(() =>
  import("../Inventory/PrintProductLabel/PrintLabelHistory/PrintLabelHistory")
);
const PrintLabelForm = lazy(() =>
  import("../Inventory/PrintProductLabel/PrintNewLabel/PrintLabelForm")
);
const InvCounts = lazy(() =>
  import("../Inventory/InventoryCount/Counts/InvCounts")
);
const AddEditInvCount = lazy(() =>
  import("../Inventory/InventoryCount/AddEditInvCount/AddEditInvCount")
);
const InvCounting = lazy(() =>
  import("../Inventory/InventoryCount/Counting/InvCounting")
);
const ReviewCounting = lazy(() =>
  import("../Inventory/InventoryCount/Counting/ReviewCounting/ReviewCounting")
);
const InventoryAdjustmentHistory = lazy(() =>
  import("../Inventory/InventoryAdjustment/List/InventoryAdjustmentHistory")
);
const InventoryAdjustmentForm = lazy(() =>
  import("../Inventory/InventoryAdjustment/Form/InventoryAdjustmentForm")
);
const SupplierBillList = lazy(() =>
  import("../Inventory/PurchaseInvoice/SupplierBillList/SupplierBillList")
);
const SupplierBillForm = lazy(() =>
  import("../Inventory/PurchaseInvoice/SupplierBillForm/SupplierBillForm")
);
const ViewSupplierBill = lazy(() =>
  import("../Inventory/PurchaseInvoice/ViewSupplierBill/ViewSupplierBill")
);

export const inventoryRoutes = (access, settings) => {
  if (!settings?.fiscalYearId) return [];

  const routes = [];

  if (access.catalogAccess) {
    routes.push(
      { path: "/stock_transfer_history", element: StockTransferHistory, title: "stockTransfers" },
      { path: "/stock_transfer_form", element: StockTransferForm, title: "stockTransferForm" },
      { path: "/stock_transfer_form/:id", element: StockTransferForm, title: "stockTransferForm" },
    );
  }

  if (access?.inventoryCountAccess) {
    routes.push(
      { path: "/inventory_counts", element: InvCounts, title: "inventoryCounts" },
      { path: "/inv_counting/:id", element: InvCounting, title: "inventoryCounting" },
      { path: "/review_counting/:id", element: ReviewCounting, title: "reviewCounting" },
      { path: "/add_edit_count", element: AddEditInvCount, title: "addInventoryCount" },
      { path: "/add_edit_count/:id", element: AddEditInvCount, title: "editInventoryCount" },
    );
  }

  if (access.printLabelAccess) {
    routes.push(
      { path: "/print_label_history", element: PrintLabelHistory, title: "printLabelHistory" },
      { path: "/print_new_label", element: PrintLabelForm, title: "printNewLabel" },
    );
  }

  // Purchase Order — gated independently of Goods Receipt.
  if (access?.stockAccess) {
    routes.push(
      { path: "/purchase_orders", element: Stocks, title: "stockControl" },
      { path: "/view_purchase_order/:id", element: ViewOrder, title: "viewOrder" },
    );

    if (access.stock?.edit) {
      routes.push({ path: "/purchase_order_form/:id", element: OrderAndReceiveForm, title: "editStockOrder" });
    }
    if (access.stock?.create) {
      routes.push({ path: "/purchase_order_form", element: OrderAndReceiveForm, title: "orderStock" });
    }
  }

  // Goods Receipt (GRN) — its own access control. The receive forms create a GRN
  // (PO-linked or standalone), so they are gated by goodsReceipt.create.
  if (access?.goodsReceiptAccess) {
    routes.push(
      { path: "/goods_receipts", element: GoodsReceipts, title: "goodsReceipts" },
      { path: "/view_goods_receipt/:id", element: ViewGoodsReceipt, title: "viewGoodsReceipt" },
    );

    if (access.goodsReceiptNav?.create) {
      routes.push(
        { path: "/receive_stock_form", element: OrderAndReceiveForm, title: "receiveStock", props: { isReceiveForm: true } },
        { path: "/receive_delivery_form/:id", element: ReceivedDeliveryForm, title: "receiveDelivery" },
      );
    }
  }

  if (access?.stockAccess) {
    routes.push(
      { path: "/special_orders", element: SpecialOrders, title: "specialOrders" },
      { path: "/special_order_form", element: SpecialOrderForm, title: "specialOrderForm" },
      { path: "/special_order_form/:id", element: SpecialOrderForm, title: "specialOrderForm" },
    );
  }

  if (access?.inventoryAdjustmentAccess) {
    routes.push(
      { path: "/inventory_adjustments", element: InventoryAdjustmentHistory, title: "inventoryAdjustments" },
      { path: "/inventory_adjustment_form", element: InventoryAdjustmentForm, title: "inventoryAdjustmentForm" },
      { path: "/inventory_adjustment_form/:id", element: InventoryAdjustmentForm, title: "inventoryAdjustmentForm" },
    );
  }

  if (access?.supplierBillAccess) {
    routes.push(
      { path: "/purchase_invoices", element: SupplierBillList, title: "purchaseInvoices" },
      { path: "/view_purchase_invoice/:id", element: ViewSupplierBill, title: "viewPurchaseInvoice" },
    );
    if (access.supplierBill?.create) {
      routes.push(
        { path: "/purchase_invoice_form", element: SupplierBillForm, title: "purchaseInvoiceForm" },
        { path: "/purchase_invoice_form/:id", element: SupplierBillForm, title: "purchaseInvoiceForm" },
      );
    }
  }

  if (access?.stockReturnAccess) {
    routes.push(
      { path: "/purchase_returns", element: StockReturns, title: "stockReturns" },
      { path: "/view_purchase_return/:id", element: ViewReturnStock, title: "viewStockReturn" },
    );
    if (access?.stockReturn?.create) {
      // No :id variant — a purchase return posts immediately and is corrected by voiding,
      // so there is nothing to re-open for editing. The old route existed but the form
      // never read useParams(), so it silently rendered a blank new return.
      routes.push(
        { path: "/purchase_return_form", element: StockReturnForm, title: "stockReturnForm" },
      );
    }
  }

  return routes;
};
